import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { api, getApiInstanceForEntity } from "../api/config";
import { useDashboardAccess } from "./DashboardAccessProvider";
import { resolveQueryTransport, transportQueryKey } from "./nativeDashboardsAccess";
import { AggregateQuerySpec } from "./types";

const AGGREGATE_STALE_TIME = 5 * 60 * 1000;

// Combinação única de filtros (fixedFilter ∧ filtro do call site) — usada pelo aggregate E pelo
// drill-down (drilldown.ts), para garantir que as linhas da modal são exatamente as do número.
export const combineFilters = (fixedFilter?: any, filter?: any) => {
    if (fixedFilter && filter) return { _and: [fixedFilter, filter] };
    const singleFilter = fixedFilter || filter;
    return singleFilter;
};

export type AggregateFetchContext = {
    dashboardKey: string;
    transport: "viewer" | "extension";
};

export const getAggregate = async (querySpec: AggregateQuerySpec, filter?: any, fetchContext?: AggregateFetchContext) => {
    const params: Record<string, any> = {};
    Object.entries(querySpec.aggregate).forEach(([operation, field]) => {
        params[`aggregate[${operation}]`] = field;
    });
    if (querySpec.groupBy?.length) {
        params.groupBy = querySpec.groupBy.join(",");
    }
    const combinedFilter = combineFilters(querySpec.fixedFilter, filter);
    if (combinedFilter) {
        params.filter = JSON.stringify(combinedFilter);
    }
    params.limit = querySpec.limit ?? -1;

    // Transporte extensão (dashboards data_scope=global): mesmo formato de params, URL da extensão
    // no backend principal — o endpoint valida a superfície e executa como admin.
    if (fetchContext?.transport === "extension") {
        const extensionResponse = await api.get(
            `native-dashboards-data/${fetchContext.dashboardKey}/aggregate/${querySpec.entity}`,
            { params }
        );
        const extensionRows = extensionResponse.data.data;
        return extensionRows;
    }

    const apiInstance = getApiInstanceForEntity(querySpec.entity);
    const response = await apiInstance.get(`items/${querySpec.entity}`, { params });
    const rows = response.data.data;
    return rows;
};

type UseAggregateOptions = {
    filter?: any;
    enabled?: boolean;
};

export const useAggregate = (querySpec: AggregateQuerySpec, options?: UseAggregateOptions) => {
    const access = useDashboardAccess();
    const transport = resolveQueryTransport(access.transport, querySpec.dataScope);
    const fetchContext: AggregateFetchContext = { dashboardKey: access.dashboardKey, transport };
    const todayKey = format(new Date(), "yyyy-MM-dd");

    return useQuery({
        queryKey: [
            "aggregate",
            transportQueryKey(transport, access.dashboardKey),
            querySpec.entity,
            JSON.stringify({ ...querySpec, filter: options?.filter }),
            todayKey,
        ],
        queryFn: () => getAggregate(querySpec, options?.filter, fetchContext),
        staleTime: AGGREGATE_STALE_TIME,
        keepPreviousData: true,
        enabled: options?.enabled,
    });
};
